import { axiosDelete, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';

import {
  CreateReplyParamsSchema,
  DeleteReplyParamsSchema,
  UpdateReplyParamsSchema,
} from '../models/reply';
import { commentQueryKeyMap } from '../utils/query-keys';

export const useCreateReply = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (params: z.infer<typeof CreateReplyParamsSchema>) => {
      const { commentId, ...payload } = CreateReplyParamsSchema.parse(params);
      return await axiosPost(`/api/comments/${commentId}/replies/`, payload);
    },
    onSuccess: async (_, { commentId }) => {
      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(commentQueryKeyMap.comment(commentId)),
      ]);
    },
  });
};

export const useUpdateReply = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (params: z.infer<typeof UpdateReplyParamsSchema>) => {
      const { id, commentId, ...payload } =
        UpdateReplyParamsSchema.parse(params);
      return await axiosPatch(
        `/api/comments/${commentId}/replies/${id}/`,
        payload,
      );
    },
    onSuccess: async (_, { commentId }) => {
      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(commentQueryKeyMap.comment(commentId)),
      ]);
    },
  });
};

export const useDeleteReply = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (params: z.infer<typeof DeleteReplyParamsSchema>) => {
      const { id, commentId } = DeleteReplyParamsSchema.parse(params);
      return await axiosDelete(`/api/comments/${commentId}/replies/${id}/`);
    },
    onSuccess: async (_, { commentId }) => {
      await Promise.all([
        invalidate(commentQueryKeyMap.commentList()),
        invalidate(commentQueryKeyMap.comment(commentId)),
      ]);
    },
  });
};
